import { useRef, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { api, type Announcement, type DeckSummary, type PortalDocument } from '../../lib/api';
import { useDepartmentStore } from '../../store/departmentStore';

/** ISO time → value for a datetime-local input, in the viewer's time zone. */
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** datetime-local value (local time) → ISO, or null when empty. */
function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

interface Props {
  open: boolean;
  /** Announcement being edited; null creates a new one. */
  editing: Announcement | null;
  sharedDecks: DeckSummary[];
  /** Department preselected for new announcements. */
  defaultCategory?: string;
  onClose: () => void;
  onSaved: () => void;
}

export function AnnouncementDialog({ open, editing, sharedDecks, defaultCategory = 'hr', onClose, onSaved }: Props) {
  const departments = useDepartmentStore((s) => s.departments);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('hr');
  const [deckId, setDeckId] = useState('');
  const [pinned, setPinned] = useState(false);
  const [requireAck, setRequireAck] = useState(false);
  const [publishAt, setPublishAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [attachmentId, setAttachmentId] = useState('');
  const [documents, setDocuments] = useState<PortalDocument[]>([]);
  const [storageEnabled, setStorageEnabled] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fill = () => {
    setTitle(editing?.title ?? '');
    setBody(editing?.body ?? '');
    setCategory(editing?.category ?? defaultCategory);
    setDeckId(editing?.deck_id ?? '');
    setPinned(editing?.pinned ?? false);
    setRequireAck(editing?.require_ack ?? false);
    setPublishAt(toLocalInput(editing?.publish_at));
    setExpiresAt(toLocalInput(editing?.expires_at));
    setAttachmentId(editing?.attachment_id ?? '');
    setError(null);
    void api.documents
      .list()
      .then((r) => {
        setDocuments(r.documents);
        setStorageEnabled(r.storageEnabled);
      })
      .catch(() => setDocuments([]));
  };

  /** Uploads a file to the documents library (in this announcement's department) and attaches it. */
  const uploadAttachment = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const title = file.name.replace(/\.[^.]+$/, '');
      const { id } = await api.documents.upload(file, { title, description: '', category });
      const r = await api.documents.list();
      setDocuments(r.documents);
      setAttachmentId(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not upload the file');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    const input = {
      title,
      body,
      category,
      deckId: deckId || null,
      pinned,
      requireAck,
      publishAt: fromLocalInput(publishAt),
      expiresAt: fromLocalInput(expiresAt),
      attachmentId: attachmentId || null,
    };
    try {
      if (editing) await api.announcements.update(editing.id, input);
      else await api.announcements.create(input);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save announcement');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="announcement-title"
      slotProps={{ transition: { onEnter: fill } }}
    >
      <DialogTitle id="announcement-title" component="h2" variant="h6">
        {editing ? 'Edit announcement' : 'New announcement'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Title"
            fullWidth
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 160 } }}
          />
          <TextField
            label="Message"
            fullWidth
            multiline
            minRows={4}
            maxRows={12}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <Stack direction="row" spacing={1.5}>
            <TextField
              select
              label="Department"
              value={departments.some((d) => d.id === category) ? category : ''}
              onChange={(e) => setCategory(e.target.value)}
              sx={{ width: 180 }}
            >
              {departments.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Linked slides (optional)"
              value={deckId}
              onChange={(e) => setDeckId(e.target.value)}
              sx={{ flex: 1 }}
              helperText="Only decks shared with everyone can be linked"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {sharedDecks.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.title}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
            <TextField
              select
              label="Attachment (optional)"
              value={documents.some((d) => d.id === attachmentId) ? attachmentId : ''}
              onChange={(e) => setAttachmentId(e.target.value)}
              sx={{ flex: 1 }}
              helperText="A file from the documents library, for staff to download"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {documents.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.title}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              color="neutral"
              disabled={uploading || !storageEnabled}
              onClick={() => fileInput.current?.click()}
              startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <AttachFileIcon />}
              sx={{ mt: 1, flexShrink: 0 }}
            >
              Upload
            </Button>
            <input
              ref={fileInput}
              type="file"
              hidden
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void uploadAttachment(file);
              }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              label="Publish at (optional)"
              type="datetime-local"
              value={publishAt}
              onChange={(e) => setPublishAt(e.target.value)}
              sx={{ flex: 1 }}
              helperText="Empty = publish now"
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Expires at (optional)"
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              sx={{ flex: 1 }}
              helperText="Empty = never expires"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>
          <Stack>
            <FormControlLabel
              control={<Switch checked={pinned} onChange={(e) => setPinned(e.target.checked)} />}
              label="Pin to the top"
            />
            <FormControlLabel
              control={<Switch checked={requireAck} onChange={(e) => setRequireAck(e.target.checked)} />}
              label={
                <Typography variant="body2">
                  Ask staff to confirm they've read it
                  <Typography component="span" variant="caption" sx={{ display: 'block' }}>
                    Shows an "I have read this" button; you can see who confirmed
                  </Typography>
                </Typography>
              }
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="neutral" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={busy || !title.trim()}
          onClick={() => void save()}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {editing ? 'Save' : 'Post announcement'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
